import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useLocation } from 'react-router-dom';
import {
  AssistantConversationSummary,
  HelpCollectionSummary,
  HelpHome,
  HelpNewsItem,
  SUPPORT_CENTER_DISPLAY_NAME,
  getHelpHome,
  listAssistantConversations,
} from '../../../services/assistantCentral';
import { SupportCenterHeaderBadge } from '../SupportCenterIcon';
import AssistantChatPanel from '../assistant/AssistantChatPanel';
import type { AssistantChatController } from '../assistant/useAssistantChat';
import MessengerBottomNav, { MessengerTab } from './MessengerBottomNav';
import MessengerHeader from './MessengerHeader';
import MessengerHome from './MessengerHome';
import MessengerMessages from './MessengerMessages';
import MessengerHelp from './MessengerHelp';
import MessengerCollection from './MessengerCollection';
import MessengerArticle from './MessengerArticle';
import { MessengerNews, MessengerNewsItem } from './MessengerNews';
import { MESSENGER } from './messengerTheme';

export type MessengerScreen =
  | { kind: 'collection'; collection: HelpCollectionSummary }
  | { kind: 'article'; slug: string }
  | { kind: 'news'; item: HelpNewsItem }
  | { kind: 'chat' };

/** Estado de navegação do messenger: aba ativa + pilha de telas empilhadas sobre ela. */
export function useMessengerNav() {
  const [tab, setTab] = useState<MessengerTab>('home');
  const [stack, setStack] = useState<MessengerScreen[]>([]);

  const push = useCallback((screen: MessengerScreen) => setStack((s) => [...s, screen]), []);
  const pop = useCallback(() => setStack((s) => s.slice(0, -1)), []);
  const switchTab = useCallback((next: MessengerTab) => {
    setTab(next);
    setStack([]);
  }, []);
  const openChat = useCallback(() => {
    setStack((s) => (s[s.length - 1]?.kind === 'chat' ? s : [...s, { kind: 'chat' }]));
  }, []);

  return { tab, stack, top: stack[stack.length - 1] as MessengerScreen | undefined, push, pop, switchTab, openChat };
}

export type MessengerNav = ReturnType<typeof useMessengerNav>;

interface SupportMessengerProps {
  nav: MessengerNav;
  chat: AssistantChatController;
  open: boolean;
  brandName: string;
  firstName?: string;
  unreadConversationIds: string[];
  chatNotice?: React.ReactNode;
  onClose: () => void;
  onNavigate: (href: string) => void;
  onOpenFullScreen: () => void;
}

const SupportMessenger: React.FC<SupportMessengerProps> = ({
  nav,
  chat,
  open,
  brandName,
  firstName,
  unreadConversationIds,
  chatNotice,
  onClose,
  onNavigate,
  onOpenFullScreen,
}) => {
  const location = useLocation();
  const [conversations, setConversations] = useState<AssistantConversationSummary[] | null>(null);
  const [helpHome, setHelpHome] = useState<HelpHome | null>(null);
  const [helpQuery, setHelpQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const unreadIds = useMemo(() => new Set(unreadConversationIds), [unreadConversationIds]);
  const { tab, top } = nav;
  const onRootTab = !top;

  useEffect(() => {
    if (!open || !onRootTab || (tab !== 'home' && tab !== 'messages')) return undefined;
    let alive = true;
    listAssistantConversations()
      .then((data) => alive && setConversations(data))
      .catch(() => alive && setConversations((prev) => prev ?? []));
    return () => {
      alive = false;
    };
  }, [open, onRootTab, tab, chat.conversationId]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    getHelpHome(location.pathname)
      .then((data) => alive && setHelpHome(data))
      .catch(() => alive && setHelpHome({ suggested: [], collections_count: 0, latest_news: null }));
    return () => {
      alive = false;
    };
  }, [open, location.pathname]);

  const openConversation = (id: string) => {
    if (id !== chat.conversationId) void chat.loadConversation(id);
    nav.openChat();
  };

  const startNewMessage = () => {
    chat.newConversation();
    nav.openChat();
  };

  const askAssistant = (question: string) => {
    chat.newConversation();
    nav.openChat();
    void chat.send(question);
  };

  const openArticle = (slug: string) => nav.push({ kind: 'article', slug });

  const searchFromHome = (query: string) => {
    setHelpQuery(query);
    nav.switchTab('help');
  };

  const renderScreen = () => {
    if (top?.kind === 'chat') {
      return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: MESSENGER.surface }}>
          <MessengerHeader
            align="left"
            onBack={nav.pop}
            onClose={onClose}
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <SupportCenterHeaderBadge size={30} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.92rem', color: MESSENGER.navy, lineHeight: 1.2 }}>
                    {SUPPORT_CENTER_DISPLAY_NAME}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '0.7rem', color: MESSENGER.slate }}>
                    {chat.humanMode ? 'Equipe Klinvia' : 'IA · responde na hora'}
                  </Typography>
                </Box>
              </Box>
            }
            actions={
              <Tooltip title="Mais opções">
                <IconButton size="small" aria-label="Mais opções" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: MESSENGER.slate }}>
                  <MoreHorizIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            }
          />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                chat.newConversation();
              }}
            >
              <ListItemIcon>
                <AddCommentOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Nova conversa</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onOpenFullScreen();
              }}
            >
              <ListItemIcon>
                <OpenInFullIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Abrir em tela cheia</ListItemText>
            </MenuItem>
          </Menu>
          <AssistantChatPanel
            chat={chat}
            greeting={`Olá${firstName ? `, ${firstName}` : ''}! Conte o que está acontecendo ou envie uma captura de tela. Respondo em passos e, se precisar, chamo a equipe Klinvia.`}
            uiVariant="classic"
            notice={chatNotice}
            onNavigate={onClose}
            onOpenHelpArticle={openArticle}
          />
        </Box>
      );
    }
    if (top?.kind === 'article') {
      return (
        <MessengerArticle
          slug={top.slug}
          onBack={nav.pop}
          onClose={onClose}
          onOpenArticle={openArticle}
          onNavigate={onNavigate}
          onAskAssistant={askAssistant}
        />
      );
    }
    if (top?.kind === 'collection') {
      return <MessengerCollection collection={top.collection} onBack={nav.pop} onClose={onClose} onOpenArticle={openArticle} />;
    }
    if (top?.kind === 'news') {
      return <MessengerNewsItem item={top.item} onBack={nav.pop} onClose={onClose} onNavigate={onNavigate} />;
    }
    if (tab === 'messages') {
      return (
        <MessengerMessages
          conversations={conversations}
          unreadIds={unreadIds}
          onClose={onClose}
          onOpenConversation={openConversation}
          onNewMessage={startNewMessage}
        />
      );
    }
    if (tab === 'help') {
      return (
        <MessengerHelp
          query={helpQuery}
          onQueryChange={setHelpQuery}
          onClose={onClose}
          onOpenCollection={(collection) => nav.push({ kind: 'collection', collection })}
          onOpenArticle={openArticle}
          onAskAssistant={askAssistant}
        />
      );
    }
    if (tab === 'news') {
      return <MessengerNews onClose={onClose} onOpenItem={(item) => nav.push({ kind: 'news', item })} />;
    }
    const recent = conversations?.[0] ?? null;
    return (
      <MessengerHome
        brandName={brandName}
        firstName={firstName}
        recentConversation={recent}
        recentUnread={Boolean(recent && unreadIds.has(recent.id))}
        suggested={helpHome ? helpHome.suggested : null}
        latestNews={helpHome?.latest_news ?? null}
        onClose={onClose}
        onOpenConversation={openConversation}
        onNewMessage={startNewMessage}
        onSearch={searchFromHome}
        onOpenArticle={openArticle}
        onOpenNews={(item) => nav.push({ kind: 'news', item })}
      />
    );
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: MESSENGER.surface }}>
      {renderScreen()}
      {top?.kind !== 'chat' && <MessengerBottomNav active={tab} onChange={nav.switchTab} unreadMessages={unreadIds.size} />}
    </Box>
  );
};

export default SupportMessenger;
